import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  ApiNetworkError,
  messageForApiError,
  UsersController_changeOwn_v1,
  type UsersController_changeOwn_v1Body,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { Card, CardContent } from '@repo/ui/components/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { useSession } from '@/auth/session';
import { optionalPinSchema } from '@/lib/field-schemas';

import { fieldLabel, fieldSurface } from './auth/AuthFrame';
import { PasswordInput } from './PasswordInput';
import { paths } from './paths';

/**
 * Replacing a credential somebody else chose: `POST /v1/users/me/credential`.
 *
 * This is the one route the API permits while `mustChangeCredential` is set. Not
 * "the one route we send them to" — the one route that answers. Every other call
 * a provisioned user could make is refused until the flag is false, which is why
 * being sent here is not a suggestion and why the screen says so in the first
 * paragraph rather than leaving somebody to work it out from a run of 403s.
 *
 * **Nothing here decides who reaches it.** The route table declares
 * `requireSignedIn({ allowsPendingCredential: true })` and the guard sends every
 * pending user here from wherever they typed — including a URL typed by hand,
 * because the decision is `decideAccess`'s and not a check inside a component
 * that a second component could forget to copy. There is no redirect in this
 * file for the same reason: two owners of one redirect is the bug FE006 removed
 * from `SignIn.tsx`, where the loser overwrote the winner's destination.
 *
 * **The gate clears on what the server reported and on nothing else.** The
 * response is `ChangeCredentialResource`, which carries `mustChangeCredential`,
 * and it is handed to the session store whole. The shape this deliberately
 * avoids: treating a 200 as proof the gate is clear. A PIN-only change can
 * legitimately leave a provisioned password — and therefore the flag — in place,
 * and an earlier server-side bug that cleared it anyway left a leaked password
 * live indefinitely. That half is fixed; this is the client half of not
 * re-creating it. So a success here can perfectly well leave the person on this
 * screen with something still to replace, and the screen says which.
 *
 * **Nothing is logged.** Not the password being replaced, not the one replacing
 * it, not the PIN, not at any level and not behind a flag. The catch below is
 * silent for the same reason the sign-in screen's is.
 */

/** Missing-input messages. These are this form's, not the API's. */
const REQUIRED_CURRENT_PASSWORD = 'Enter the password you signed in with.';
const PASSWORDS_DIFFER = 'The two new passwords do not match.';
const PINS_DIFFER = 'The two new PINs do not match.';

/**
 * Neither field filled. The API would refuse the call; saying so here is both
 * faster and more specific than the refusal that would come back.
 */
const NOTHING_TO_CHANGE =
  'Enter a new password, a new PIN, or both — there is nothing to save yet.';

/** Not a refusal: nothing came back at all. Worded as a connection to check. */
const UNREACHABLE =
  'Could not reach the server. Check the network connection and try again — nothing was changed.';

/** Neither a refusal nor the network. It says so rather than blaming the API. */
const UNEXPECTED =
  'The change could not be completed. Try again, and report it if it keeps happening.';

/**
 * What this screen will send, and what it refuses before sending.
 *
 * The two confirmations are `superRefine`d rather than checked field by field,
 * because "these two do not match" is a statement about a pair and zod's
 * per-field rules cannot see a sibling. Each refusal is still attached to the
 * confirm field — that is where somebody looks and that is what they retype.
 *
 * **The PIN rule is imported, not restated.** It lived here *and* in
 * `routes/users/user-roles.ts`, written twice by two workers who could not see
 * each other; `lib/field-schemas.ts` is now the only copy. There is deliberately
 * still no rule for the password: the contract states no length, and inventing
 * one would refuse a password the API would have accepted.
 */
const changeCredentialSchema = z
  .object({
    currentPassword: z.string().min(1, REQUIRED_CURRENT_PASSWORD),
    newPassword: z.string(),
    confirmPassword: z.string(),
    newPin: optionalPinSchema,
    confirmPin: z.string(),
  })
  .superRefine((values, context) => {
    if (values.newPassword !== '' && values.confirmPassword !== values.newPassword) {
      context.addIssue({
        code: 'custom',
        path: ['confirmPassword'],
        message: PASSWORDS_DIFFER,
      });
    }

    if (values.newPin !== '' && values.confirmPin !== values.newPin) {
      context.addIssue({ code: 'custom', path: ['confirmPin'], message: PINS_DIFFER });
    }
  });

type ChangeCredentialValues = z.infer<typeof changeCredentialSchema>;

/** What the last successful call actually set — used only to word the result. */
interface Saved {
  readonly password: boolean;
  readonly pin: boolean;
}

export function ChangeCredential() {
  const client = useApiClient();
  const { session, credentialChanged } = useSession();

  const form = useForm({
    resolver: zodResolver(changeCredentialSchema),
    mode: 'onTouched',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      newPin: '',
      confirmPin: '',
    },
  });

  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);

  const pending = form.formState.isSubmitting;

  // Read from the session rather than from a local flag, every render. This is
  // the value the route guard is deciding on, so what this screen says about
  // being locked out of the rest of the app cannot disagree with whether it
  // actually is. It changes only when the server says so.
  const mustChange = session?.user.mustChangeCredential ?? false;

  const handleSubmit = async ({ currentPassword, newPassword, newPin }: ChangeCredentialValues) => {
    // Belt and braces with the disabled button. A second submit would send the
    // now-replaced password as `currentPassword` and be refused, which reads as
    // "your password is wrong" moments after it was accepted.
    if (pending) return;

    // The previous result stops being the current one the moment they submit
    // again. Left up, "your PIN has been set" would sit above a failure.
    setSaved(null);
    setFormError(null);

    // Not a field's problem — it is a statement about the form as a whole, so it
    // goes above the submit rather than beside any one input. Kept out of the
    // schema for that reason: a `superRefine` would have to pick a field to
    // hang it on, and neither of the two is the one at fault.
    if (newPassword === '' && newPin === '') {
      setFormError(NOTHING_TO_CHANGE);

      return;
    }

    try {
      // Body and response shapes both come from the generated client. Nothing
      // about `/v1/users/me/credential` is written down in this file — omitting
      // a field is how "leave this one alone" is said, so the two optional
      // fields are present only when there is something to set.
      const body: UsersController_changeOwn_v1Body = {
        currentPassword,
        ...(newPassword === '' ? {} : { newPassword }),
        ...(newPin === '' ? {} : { newPin }),
      };

      const reported = await client.request(UsersController_changeOwn_v1, { body });

      // The authoritative answer, handed over whole. `reported` carries
      // `mustChangeCredential` and it is the only thing that clears the gate —
      // there is deliberately no branch here that sets it false because the call
      // succeeded, and no navigation either. If the flag is now false, the guard
      // stops holding the rest of the app shut and the shell's navigation comes
      // back on the very next render.
      credentialChanged(reported);

      setSaved({ password: newPassword !== '', pin: newPin !== '' });

      // Cleared on success, all five, and reset rather than set one at a time —
      // which also clears the touched and dirty bookkeeping, so the emptied
      // fields do not immediately report being empty. The current password is
      // stale the moment a password change lands, and leaving either new value
      // in a field invites a second submit that re-sends it against a credential
      // that has moved.
      form.reset();
    } catch (error) {
      // Silent on purpose. Three credentials passed through this function and
      // none of them is written anywhere, including here. The code is what gets
      // worded, by `messageForApiError`, from the code alone.
      if (error instanceof ApiError) {
        setFormError(messageForApiError(error));
      } else if (error instanceof ApiNetworkError) {
        setFormError(UNREACHABLE);
      } else {
        setFormError(UNEXPECTED);
      }

      // Nothing is cleared on a failure: the retry is usually one corrected
      // field, and re-typing four of them to fix the fifth is a cruelty this
      // screen is in a poor position to inflict.
    }
  };

  return (
    // `my-auto` rather than `items-center`, because this form is five fields,
    // five hints and a result notice deep. Centred flex content that outgrows
    // its container overflows past the top edge and cannot be scrolled back to,
    // which on this screen would hide the "current password" field — the one
    // field nothing can be saved without.
    <main className="flex flex-1 justify-center p-6">
      <Card className="my-auto w-full max-w-sm">
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <h1 className="font-heading text-2xl font-medium tracking-tight text-foreground">
              {mustChange ? 'Change your credential' : 'Change your password or PIN'}
            </h1>
            <div className="!mt-4 h-[2px] w-10 bg-foreground" />

            {/* Why this screen, in the words of what actually happened. A
                provisioned user has not done anything wrong and is not being
                warned; they are being told that the rest of the back office is
                shut until this is done, because it is, and because otherwise
                every control they try answers with a refusal they cannot act
                on. */}
            <p className="text-sm text-muted-foreground">
              {mustChange
                ? 'You signed in with a credential that was set for you by someone else. Replace it before you go any further — until you do, this is the only page in the back office that will open.'
                : 'Set a new back-office password, a new terminal PIN, or both. Leave one blank to keep it as it is.'}
            </p>
          </div>

          {/* One column, per docs/ui-conventions.md. */}
          <Form {...form}>
            <form
              className="space-y-5"
              noValidate
              onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
            >
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    {/* The only required field on this screen. The four below it
                        are each optional on their own — what is not optional is
                        leaving all four blank, and that is a statement about the
                        form rather than about any one of them. */}
                    <FormLabel required className={fieldLabel}>
                      Current password
                    </FormLabel>
                    <FormControl
                      render={
                        <PasswordInput
                          className={`${fieldSurface} h-11`}
                          required
                          placeholder="The password you signed in with"
                          autoComplete="current-password"
                          {...field}
                        />
                      }
                    />
                    <FormDescription>
                      Your current back-office password, even if you are only setting a PIN.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={fieldLabel}>New password</FormLabel>
                    <FormControl
                      render={
                        <PasswordInput
                          className={`${fieldSurface} h-11`}
                          // No shape to show: the API publishes no bound and
                          // this screen must not invent one. It says what the
                          // field is for instead.
                          placeholder="Leave blank to keep your password"
                          autoComplete="new-password"
                          {...field}
                        />
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={fieldLabel}>Confirm new password</FormLabel>
                    <FormControl
                      render={
                        <PasswordInput
                          className={`${fieldSurface} h-11`}
                          placeholder="Type the new password again"
                          autoComplete="new-password"
                          {...field}
                        />
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newPin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={fieldLabel}>New terminal PIN</FormLabel>
                    <FormControl
                      render={
                        <Input
                          type="password"
                          // Digits, read and typed one at a time, so they get
                          // the tabular figures every other code in this app
                          // gets. `inputMode` puts the numeric keypad up on a
                          // touchscreen without making this a number field,
                          // which would strip a leading zero.
                          className={`${fieldSurface} h-11 font-mono tracking-wider numeric`}
                          placeholder="4827"
                          inputMode="numeric"
                          autoComplete="new-password"
                          {...field}
                        />
                      }
                    />
                    <FormDescription>
                      4 to 6 digits. This is what you type at a till, not in the back office.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={fieldLabel}>Confirm new PIN</FormLabel>
                    <FormControl
                      render={
                        <Input
                          type="password"
                          className={`${fieldSurface} h-11 font-mono tracking-wider numeric`}
                          placeholder="Type the new PIN again"
                          inputMode="numeric"
                          autoComplete="new-password"
                          {...field}
                        />
                      }
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Above the submit and it stays there, per the conventions: a
                  toast vanishes and a staff member who looked away cannot get it
                  back. */}
              {formError !== null && (
                <Alert variant="destructive">
                  <IconAlertTriangle />
                  <AlertDescription>{formError}</AlertDescription>
                </Alert>
              )}

              {saved !== null && <SavedNotice saved={saved} mustChange={mustChange} />}

              <Button type="submit" className="w-full" disabled={pending}>
                {pending ? 'Saving…' : 'Save'}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </main>
  );
}

/**
 * What the server said happened, and what is still outstanding.
 *
 * `mustChange` is read from the session — that is, from the response the API
 * just gave — and not from what was submitted. The two-branch shape here is the
 * whole point of the ticket rendered: a change can succeed and leave the gate
 * shut, and the person in front of it needs to be told that rather than left
 * wondering why the navigation has not come back.
 */
function SavedNotice({ saved, mustChange }: { saved: Saved; mustChange: boolean }) {
  const both = saved.password && saved.pin;
  const what = both ? 'password and PIN' : saved.password ? 'password' : 'PIN';

  return (
    <Alert>
      {mustChange ? <IconAlertTriangle /> : <IconCircleCheck />}
      <AlertTitle>
        Your new {what} {both ? 'have' : 'has'} been set
      </AlertTitle>
      <AlertDescription>
        {mustChange ? (
          <p>
            There is still a credential on this account that someone else chose, so the rest of the
            back office stays closed. Set the one you have not replaced yet.
          </p>
        ) : (
          <>
            <p>The rest of the back office is open to you now.</p>
            <Button render={<Link to={paths.dashboard} />}>Continue</Button>
          </>
        )}
      </AlertDescription>
    </Alert>
  );
}
